import { type KeyboardEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Loader2, Star } from "lucide-react";
import { AppShell, type AppShellContext } from "@/components/app/AppShell";
import { TrialBanner } from "@/components/customer/TrialBanner";
import { ViolationBadge } from "@/components/audit/ViolationBadge";
import { CopyReviewUrlButton } from "@/components/customer/CopyReviewUrlButton";
import { TakedownStatusControls } from "@/components/customer/TakedownStatusControls";
import { api } from "@/lib/api";
import { SetupChecklist } from "@/components/customer/SetupChecklist";
import type { BrandAmazonAccess } from "@/lib/amazonAccess";
import {
  amazonTakedownCapability,
  canAuthorizeManagedTakedowns,
} from "@/lib/amazonAccess";

type BrandSummary = BrandAmazonAccess & {
  id: string;
  name: string;
  asinCount: number;
  flaggedThisMonth: number;
  takedownsSubmitted: number;
  removed: number;
};

type BrandReview = {
  id: string;
  asin: string;
  amazonReviewId: string;
  reviewUrl: string | null;
  productTitle: string | null;
  rating: number;
  title: string | null;
  body: string;
  reviewerName: string | null;
  verifiedPurchase: boolean;
  createdAt: string;
  classification: {
    violations: { type: string }[] | null;
    rationale: string | null;
    actionStatus: string;
    submittedAt: string | null;
    removedAt: string | null;
    chargeStatus?: string;
  } | null;
};

type TrialStatus = {
  tier: string;
  status: string;
  trialEndsAt: string | null;
  skuCap: number | null;
  scrapeCadence: string;
  monitoredAsinCount: number;
};

type StatusUpdate = {
  actionStatus: string;
  submittedAt: string | null;
  removedAt: string | null;
  chargeStatus?: string | null;
};

export default function DashboardOverview() {
  return (
    <AppShell>{(ctx) => <Inner ctx={ctx} />}</AppShell>
  );
}

function Inner({ ctx }: { ctx: AppShellContext }) {
  const [summary, setSummary] = useState<BrandSummary | null>(null);
  const [reviews, setReviews] = useState<BrandReview[] | null>(null);
  const [trial, setTrial] = useState<TrialStatus | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!ctx.activeBrandId) return;
    let cancelled = false;
    setSummary(null);
    setReviews(null);
    api<{ brands: BrandSummary[] }>("/customer/brands").then((res) => {
      if (cancelled) return;
      const brand = res.data?.brands.find((b) => b.id === ctx.activeBrandId);
      setSummary(brand ?? null);
    });
    api<{ reviews: BrandReview[] }>(
      `/customer/brands/${ctx.activeBrandId}/reviews`
    ).then((res) => {
      if (cancelled) return;
      setReviews(res.data?.reviews ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [ctx.activeBrandId]);

  useEffect(() => {
    let cancelled = false;
    api<{ subscription: TrialStatus | null }>("/customer/trial-status").then(
      (res) => {
        if (cancelled) return;
        setTrial(res.data?.subscription ?? null);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  function applyStatus(reviewId: string, next: StatusUpdate) {
    const previous = reviews?.find((review) => review.id === reviewId);
    const wasSubmitted = previous?.classification?.actionStatus === "submitted";
    const isSubmitted = next.actionStatus === "submitted";

    setReviews((prev) =>
      prev
        ? prev.map((review) =>
            review.id === reviewId && review.classification
              ? {
                  ...review,
                  classification: {
                    ...review.classification,
                    actionStatus: next.actionStatus,
                    submittedAt: next.submittedAt,
                    removedAt: next.removedAt,
                    ...(next.chargeStatus !== undefined
                      ? { chargeStatus: next.chargeStatus ?? undefined }
                      : {}),
                  },
                }
              : review
          )
        : prev
    );

    if (wasSubmitted !== isSubmitted) {
      setSummary((prev) =>
        prev
          ? {
              ...prev,
              takedownsSubmitted: Math.max(
                0,
                prev.takedownsSubmitted + (isSubmitted ? 1 : -1)
              ),
            }
          : prev
      );
    }
  }

  if (ctx.brands.length === 0) {
    return (
      <div className="mx-auto w-full max-w-3xl px-6 py-16 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          No brands yet
        </h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Add your first brand to start monitoring your Amazon catalog.
        </p>
        <Link
          to="/dashboard/brands/new"
          className="mt-6 inline-flex rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
        >
          + Add a brand
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-6 py-8">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            Dashboard
          </h1>
          {summary && (
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{summary.name}</p>
          )}
        </div>
      </div>

      {trial?.status === "trialing" && (
        <div className="mt-6">
          <TrialBanner
            trialEndsAt={trial.trialEndsAt}
            monitoredAsinCount={trial.monitoredAsinCount}
            skuCap={trial.skuCap}
            scrapeCadence={trial.scrapeCadence}
          />
        </div>
      )}

      {summary && (
        <div className="mt-6">
          <SetupChecklist brands={[summary]} />
        </div>
      )}

      {summary && (
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Tile label="Products monitored" value={summary.asinCount} />
          <Tile label="Flagged this month" value={summary.flaggedThisMonth} />
          <Tile label="Takedowns submitted" value={summary.takedownsSubmitted} />
          <Tile label="Removed" value={summary.removed} accent />
        </div>
      )}

      <section className="mt-10">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Recent flagged reviews
        </h2>
        <div className="mt-3 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
          {reviews === null && (
            <div className="flex items-center gap-2 px-5 py-6 text-sm text-slate-500 dark:text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {reviews && reviews.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-slate-600 dark:text-slate-400">
              Nothing flagged yet for this brand. Reviews are scanned weekly.
            </div>
          )}
          {summary && reviews?.map((r) => (
            <ReviewRow
              key={r.id}
              review={r}
              brand={summary}
              expanded={expanded === r.id}
              onToggle={() =>
                setExpanded((cur) => (cur === r.id ? null : r.id))
              }
              onStatusChange={applyStatus}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function Tile({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div
      className={
        accent
          ? "rounded-2xl border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900 dark:bg-emerald-950/40"
          : "rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900"
      }
    >
      <div
        className={
          accent
            ? "text-xs font-medium text-emerald-700 dark:text-emerald-300"
            : "text-xs font-medium text-slate-500 dark:text-slate-400"
        }
      >
        {label}
      </div>
      <div
        className={
          accent
            ? "mt-2 text-3xl font-semibold tracking-tight text-emerald-900 dark:text-emerald-200"
            : "mt-2 text-3xl font-semibold tracking-tight text-slate-900 dark:text-slate-100"
        }
      >
        {value}
      </div>
    </div>
  );
}

const STATUS_PILL: Record<string, { label: string; cls: string }> = {
  none: { label: "Awaiting authorization", cls: "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700" },
  watching: { label: "Watching", cls: "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:ring-blue-900" },
  authorized: { label: "Authorized · awaiting filing", cls: "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900" },
  submitted: { label: "Submitted to Amazon", cls: "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900" },
  removed: { label: "Removed", cls: "bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-900" },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-800 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900" },
};

function pillKey(c: BrandReview["classification"]): string {
  if (!c) return "none";
  if (c.actionStatus === "removed") return "removed";
  return c.actionStatus;
}

function ReviewRow({
  review,
  brand,
  expanded,
  onToggle,
  onStatusChange,
}: {
  review: BrandReview;
  brand: BrandSummary;
  expanded: boolean;
  onToggle: () => void;
  onStatusChange: (reviewId: string, next: StatusUpdate) => void;
}) {
  const [authorizing, setAuthorizing] = useState(false);
  const [authorizeError, setAuthorizeError] = useState<string | null>(null);

  async function authorize() {
    setAuthorizeError(null);
    setAuthorizing(true);
    const res = await api<{ ok: true; status: string }>(
      `/customer/reviews/${review.id}/authorize-takedown`,
      { method: "POST", body: JSON.stringify({ brandId: brand.id }) }
    );
    setAuthorizing(false);
    if (!res.ok) {
      setAuthorizeError(res.error ?? "Couldn't authorize.");
      return;
    }
    onStatusChange(review.id, {
      actionStatus: "authorized",
      submittedAt: null,
      removedAt: null,
      chargeStatus: "not_billable",
    });
  }

  const awaitingAuthorization =
    review.classification?.actionStatus === "none" ||
    review.classification?.actionStatus === "watching";
  const takedownsReady = canAuthorizeManagedTakedowns(brand);
  const canAuthorize = awaitingAuthorization && takedownsReady;
  const meta = STATUS_PILL[pillKey(review.classification)] ?? STATUS_PILL.none;
  const violations = review.classification?.violations ?? [];
  function onRowKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle();
    }
  }

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={onRowKeyDown}
        className="flex w-full cursor-pointer items-start gap-4 px-5 py-4 text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:hover:bg-slate-800"
      >
        <div className="flex w-32 shrink-0 flex-col items-start gap-1">
          <StarRating rating={review.rating} />
          <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400">{review.asin}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3">
            <span className="font-medium text-slate-900 dark:text-slate-100">
              {review.productTitle || review.asin}
            </span>
            {review.reviewUrl && (
              <span className="inline-flex items-center gap-1">
                <a
                  href={review.reviewUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                >
                  Amazon review <ExternalLink className="h-3 w-3" />
                </a>
                <CopyReviewUrlButton url={review.reviewUrl} />
              </span>
            )}
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {review.verifiedPurchase ? "Verified" : "Unverified"}
              {review.reviewerName ? ` · ${review.reviewerName}` : ""}
            </span>
          </div>
          <p className="mt-1 line-clamp-1 text-sm text-slate-600 dark:text-slate-400">
            {review.title ? `"${review.title}" — ` : ""}
            {review.body.slice(0, 140)}
            {review.body.length > 140 ? "…" : ""}
          </p>
          {violations.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {violations.map((v, i) => (
                <ViolationBadge key={i} type={v.type} />
              ))}
            </div>
          )}
        </div>
        <div className="ml-2 flex shrink-0 flex-col items-end gap-1">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${meta.cls}`}
          >
            {meta.label}
          </span>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {new Date(review.createdAt).toLocaleDateString()}
          </span>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-950">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Full review
              </div>
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">"{review.body}"</p>
              {review.classification?.rationale && (
                <>
                  <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Why we flagged this
                  </div>
                  <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                    {review.classification.rationale}
                  </p>
                </>
              )}
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Take action
              </div>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                For takedown-ready brands, authorize Reputely to include this
                review in a manual Brand Registry filing.
              </p>

              {!awaitingAuthorization && (
                <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                  This review's already been authorized — nothing to do here.
                </p>
              )}

              {awaitingAuthorization && !takedownsReady && (
                <div className="mt-3 rounded-xl bg-amber-50 p-4 text-sm text-amber-900 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-900">
                  <p>
                    {amazonTakedownCapability(brand) === "unverified_no_brand_registry"
                      ? "Managed takedowns are not yet supported for this brand without Brand Registry."
                      : "Complete Brand Registry setup before authorizing managed takedowns."}
                  </p>
                  <Link
                    to={`/dashboard/onboarding?brand=${encodeURIComponent(brand.id)}`}
                    className="mt-2 inline-flex font-medium underline underline-offset-4"
                  >
                    View Amazon setup
                  </Link>
                </div>
              )}

              {canAuthorize && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={authorize}
                    disabled={authorizing}
                    className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
                  >
                    {authorizing ? "Authorizing…" : "Authorize takedown →"}
                  </button>
                  {authorizeError && (
                    <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                      {authorizeError}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Included with your monitoring plan.
                  </p>
                </div>
              )}
              {review.classification?.actionStatus === "authorized" && (
                <TakedownStatusControls
                  reviewId={review.id}
                  actionStatus={review.classification.actionStatus}
                  onChange={(next) => onStatusChange(review.id, next)}
                  className="mt-4"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StarRating({ rating }: { rating: number }) {
  const safe = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <span
      className="inline-flex items-center gap-0.5"
      aria-label={`${safe} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3.5 w-3.5 ${
            n <= safe
              ? "fill-amber-400 text-amber-400"
              : "fill-slate-200 text-slate-200 dark:fill-slate-700 dark:text-slate-700"
          }`}
        />
      ))}
    </span>
  );
}
